import { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Shield, Copy, Check } from 'lucide-react';

interface ScanMFAProps {
  qrCodeUrl: string;
  secret: string;
  onComplete: () => void;
}

export function ScanMFA({ qrCodeUrl, secret, onComplete }: ScanMFAProps) {
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopySecret = () => {
    navigator.clipboard.writeText(secret);
    setCopied(true);
    toast.success('Secret copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleVerify = async () => {
    if (verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setLoading(true);

    try {
      console.log('[ScanMFA] Verifying code...');
      
      // Verify the code
      const { data, error } = await supabase.functions.invoke('verify-mfa', {
        body: { code: verificationCode }
      });

      console.log('[ScanMFA] Response:', { data, error });

      if (error) {
        console.error('[ScanMFA] Function error:', error);
        throw error;
      }

      if (data?.error) {
        console.error('[ScanMFA] Data error:', data.error);
        toast.error(data.error);
        setVerificationCode('');
        return;
      }

      if (data?.success) {
        // Enable MFA in database
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          console.log('[ScanMFA] Enabling MFA for user:', user.id);
          const { error: updateError } = await supabase
            .from('mfa_secrets')
            .update({ enabled: true })
            .eq('user_id', user.id);
          
          if (updateError) {
            console.error('[ScanMFA] Failed to enable MFA:', updateError);
            throw updateError;
          }
        }

        toast.success('MFA setup completed successfully!');
        onComplete();
      } else {
        toast.error('Invalid verification code. Please try again.');
        setVerificationCode('');
      }
    } catch (error: any) {
      console.error('[ScanMFA] Verification error:', error);
      
      // Handle specific error types
      if (error.message?.includes('MFA not configured')) {
        toast.error('MFA setup incomplete. Please scan the QR code again.');
      } else if (error.message?.includes('Invalid code format')) {
        toast.error('Please enter a valid 6-digit code');
      } else if (error.message?.includes('Unauthorized')) {
        toast.error('Session expired. Please sign up again.');
      } else {
        toast.error(error.message || 'Failed to verify code. Please try again.');
      }
      
      setVerificationCode('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-6">
      <div className="mb-6 flex flex-col items-center text-center">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
          <Shield className="h-8 w-8 text-primary" />
        </div>
        <h2 className="text-xl font-semibold">Set Up Two-Factor Authentication</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Scan this QR code with your authenticator app
        </p>
      </div>

      <div className="space-y-6">
        {/* QR Code */}
        <div className="flex justify-center rounded-lg border bg-white p-4">
          <QRCodeSVG value={qrCodeUrl} size={200} />
        </div>

        {/* Manual Entry */}
        <div>
          <p className="mb-2 text-sm font-medium">Or enter this code manually:</p>
          <div className="flex items-center gap-2">
            <Input
              value={secret}
              readOnly
              className="font-mono text-sm"
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={handleCopySecret}
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        {/* Verification */}
        <div>
          <p className="mb-2 text-sm font-medium">Enter verification code:</p>
          <Input
            type="text"
            placeholder="000000"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            maxLength={6}
            className="text-center text-lg tracking-widest"
          />
        </div>

        <Button
          onClick={handleVerify}
          disabled={loading || verificationCode.length !== 6}
          className="w-full"
        >
          {loading ? 'Verifying...' : 'Complete Setup'}
        </Button>

        <div className="rounded-lg bg-muted p-4 text-sm">
          <p className="font-medium">Recommended authenticator apps:</p>
          <ul className="mt-2 list-inside list-disc space-y-1 text-muted-foreground">
            <li>Google Authenticator</li>
            <li>Microsoft Authenticator</li>
            <li>Authy</li>
          </ul>
        </div>
      </div>
    </Card>
  );
}